/**
 * Hook to merge Wix conversation messages with SMS and call logs
 * Fetches calls/SMS from Firestore and merges them by timestamp
 */

import { useEffect, useState } from 'react';
import { useAuth } from '../contexts';
import { getFirestore, collection, query, where, onSnapshot, Timestamp } from 'firebase/firestore';
import { StaffCacheService } from '@rsmaids/shared/services/api/staffCacheService';
import i18n from '../i18n';

interface CallLogItem {
  id: string;
  callId: string;
  customerId?: string;
  customerNumber: string;
  callerName?: string;
  direction: 'inbound' | 'outbound';
  status: string;
  duration: number;
  recordingUrl?: string;
  transcriptionUrl?: string;
  twilioCallSid: string;
  startTime: Date;
  endTime: Date;
}

interface SmsItem {
  id: string;
  customerId?: string;
  customerNumber: string;
  messageText: string;
  direction: 'inbound' | 'outbound';
  timestamp: Date;
  status: string;
  twilioSid?: string;
}

interface UnifiedConversationItem {
  id: string;
  type: 'wix' | 'call' | 'sms';
  timestamp: Date;
  // For Wix messages
  from?: string;
  text?: string;
  channel?: string;
  isFromCustomer?: boolean;
  avatarUrl?: string;
  senderStaffId?: string;
  senderStaffName?: string;
  senderStaffAvatar?: string;
  senderType?: string;
  // For calls
  callId?: string;
  direction?: 'inbound' | 'outbound';
  duration?: number;
  recordingUrl?: string;
  callerName?: string;
  // For SMS
  messageText?: string;
  customerNumber?: string;
}

interface UseConversationUnifiedProps {
  conversationId: string;
  contactId: string; // Required - the Wix contact ID associated with the conversation
  enabled: boolean;
}

/**
 * Get customer ID for a conversation
 * Simply returns the provided contactId
 */
const getCustomerIdFromConversation = (contactId: string): string => {
  console.log(`✅ Using contact ID:`, contactId);
  return contactId;
};

/**
 * Hook to fetch calls and SMS for a conversation customer
 * The contactId is automatically enriched by the conversation updater Cloud Function
 * which processes Pub/Sub events and updates conversation documents with contactId
 */
export const useConversationUnified = ({
  conversationId,
  contactId,
  enabled,
}: UseConversationUnifiedProps) => {
  const { user } = useAuth();
  const [unifiedItems, setUnifiedItems] = useState<
    { calls: CallLogItem[]; sms: SmsItem[] }
  >({ calls: [], sms: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Not-ready early-out is computed during render (React's "adjust state
  // when a dependency changes" pattern) rather than as a setState call
  // inside the effect below, which now only handles the actual
  // fetch/subscribe for a ready conversation.
  const canSubscribe = Boolean(enabled && conversationId && user);
  const [prevCanSubscribe, setPrevCanSubscribe] = useState(canSubscribe);
  if (prevCanSubscribe !== canSubscribe) {
    setPrevCanSubscribe(canSubscribe);
    if (!canSubscribe) setLoading(false);
  }

  // Fetch customer ID and subscribe to unified items
  useEffect(() => {
    if (!canSubscribe || !user) return;

    let unsubscribeCalls: (() => void) | null = null;
    let unsubscribeSms: (() => void) | null = null;
    let isMounted = true;

    const subscribe = async () => {
      try {
        // Get staffMemberId from staff cache
        const staffCache = StaffCacheService.getInstance();
        const isLoaded = await staffCache.isStaffCacheLoaded();
        if (!isLoaded) {
          await staffCache.loadStaffCache();
        }

        // Get current staff member
        const currentStaff = staffCache.getStaffByWixUserId(user.id);
        if (!currentStaff?.staffMemberId) {
          console.warn('No staff member found for user:', user.id);
          setLoading(false);
          return;
        }

        // Get customer ID from conversation (synchronous - no async calls)
        const customerId = getCustomerIdFromConversation(contactId);

        if (!customerId) {
          console.warn('No customer ID provided for conversation:', conversationId);
          setLoading(false);
          return;
        }

        // Use the named 'twilio-communications' database
        const db = getFirestore('twilio-communications');

        // Subscribe to call logs - filtered by staffMemberId and customerId
        const callsQuery = query(
          collection(db, 'callLogs'),
          where('staffMemberId', '==', currentStaff.staffMemberId),
          where('customerId', '==', customerId)
        );

        const subscribeToCallLogs = () => {
          if (!isMounted) return;
          unsubscribeCalls = onSnapshot(
            callsQuery,
            (snapshot) => {
              if (isMounted) {
                const calls = snapshot.docs.map((doc) => {
                  const data = doc.data();
                  return {
                    id: doc.id,
                    callId: data.callId,
                    customerId: data.customerId,
                    customerNumber: data.customerNumber,
                    callerName: data.callerName,
                    direction: data.direction,
                    status: data.status,
                    duration: data.duration || 0,
                    recordingUrl: data.recordingUrl,
                    transcriptionUrl: data.transcriptionUrl,
                    twilioCallSid: data.twilioCallSid,
                    startTime:
                      data.startedAt instanceof Timestamp
                        ? data.startedAt.toDate()
                        : new Date(data.startedAt || data.timestamp),
                    endTime:
                      data.endTime instanceof Timestamp
                        ? data.endTime.toDate()
                        : new Date(data.timestamp || data.startedAt),
                  } as CallLogItem;
                });

                setUnifiedItems((prev) => ({ ...prev, calls }));
              }
            },
            (error) => {
              console.error('Error fetching calls, resubscribing in 5s:', error);
              if (isMounted) setError(i18n.t('hooks.useConversationUnified.failedToFetchCalls'));
              setTimeout(subscribeToCallLogs, 5000);
            }
          );
        };
        subscribeToCallLogs();

        // Subscribe to SMS messages - filtered by staffMemberId and customerId
        const smsQuery = query(
          collection(db, 'smsMessages'),
          where('staffMemberId', '==', currentStaff.staffMemberId),
          where('customerId', '==', customerId)
        );

        const subscribeToSms = () => {
          if (!isMounted) return;
          unsubscribeSms = onSnapshot(
            smsQuery,
            (snapshot) => {
              if (isMounted) {
                const sms = snapshot.docs.map((doc) => {
                  const data = doc.data();
                  return {
                    id: doc.id,
                    customerId: data.customerId,
                    customerNumber: data.customerNumber,
                    messageText: data.messageText || data.text || data.body,
                    direction: data.direction,
                    timestamp:
                      data.timestamp instanceof Timestamp
                        ? data.timestamp.toDate()
                        : new Date(data.timestamp),
                    status: data.status,
                    twilioSid: data.twilioSid || data.sid,
                  } as SmsItem;
                });

                setUnifiedItems((prev) => ({ ...prev, sms }));
              }
            },
            (error) => {
              console.error('Error fetching SMS, resubscribing in 5s:', error);
              if (isMounted) setError(i18n.t('hooks.useConversationUnified.failedToFetchSms'));
              setTimeout(subscribeToSms, 5000);
            }
          );
        };
        subscribeToSms();

        setLoading(false);
      } catch (err) {
        console.error('Error setting up unified items subscription:', err);
        setError(i18n.t('hooks.useConversationUnified.failedToSetUpSubscriptions'));
        setLoading(false);
      }
    };

    subscribe();

    return () => {
      isMounted = false;
      if (unsubscribeCalls) unsubscribeCalls();
      if (unsubscribeSms) unsubscribeSms();
    };
  }, [conversationId, contactId, canSubscribe, user]);

  return { unifiedItems, loading, error };
};

/**
 * Merge Wix messages with calls and SMS, sorted by timestamp
 */
export const mergeConversationItems = (
  wxMessages: Array<{
    id?: string;
    from: string;
    text: string;
    channel: string;
    _createdDate: Date;
    isFromCustomer?: boolean;
    avatarUrl?: string;
    senderStaffId?: string;
    senderStaffName?: string;
    senderStaffAvatar?: string;
    senderType?: string;
  }>,
  calls: CallLogItem[],
  sms: SmsItem[]
): UnifiedConversationItem[] => {
  const items: UnifiedConversationItem[] = [];

  // Add Wix messages
  wxMessages.forEach((msg) => {
    items.push({
      id: msg.id || `wix-${msg._createdDate.getTime()}`,
      type: 'wix',
      timestamp: msg._createdDate,
      from: msg.from,
      text: msg.text,
      channel: msg.channel,
      isFromCustomer: msg.isFromCustomer,
      avatarUrl: msg.avatarUrl,
      senderStaffId: msg.senderStaffId,
      senderStaffName: msg.senderStaffName,
      senderStaffAvatar: msg.senderStaffAvatar,
      senderType: msg.senderType,
    });
  });

  // Add calls
  calls.forEach((call) => {
    items.push({
      id: `call-${call.callId}`,
      type: 'call',
      timestamp: call.startTime,
      callId: call.callId,
      direction: call.direction,
      duration: call.duration,
      recordingUrl: call.recordingUrl,
      callerName: call.callerName,
      customerNumber: call.customerNumber,
    });
  });

  // Add SMS
  sms.forEach((s) => {
    items.push({
      id: `sms-${s.id}`,
      type: 'sms',
      timestamp: s.timestamp,
      direction: s.direction,
      messageText: s.messageText,
      customerNumber: s.customerNumber,
    });
  });

  // Sort by timestamp descending (newest first)
  return items.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
};
